import groupBy from 'lodash/groupBy';
import { ListCreator } from './useScorableItemFilters.d';

export type CheckboxState = 'checked' | 'unchecked' | 'indeterminate';

type ScorableItemsState = Record<string, CheckboxState>;

interface ScorableItem {
  id: string;
  nodeId: string;
}
interface Node {
  id: string;
  parentNodeId?: string;
  nodes?: Node[];
  scorableItems?: ScorableItem[];
}

export function getParentOfNode<T extends Node>(nodeId: string, flattenedSourceTrees: T[]): T | null {
  const childNode = flattenedSourceTrees.find(node => node.id === nodeId);
  if (!childNode) return null;

  return (flattenedSourceTrees.find(node => node.id === childNode.parentNodeId) as T | undefined) || null;
}

export function nodesSorterFactory(referenceNodes: Node[]) {
  return (node1: Node, node2: Node) => {
    return (
      referenceNodes.findIndex(node => node.id === node1.id) - referenceNodes.findIndex(node => node.id === node2.id)
    );
  };
}

function buildAncestorTreeOfNode<T extends Node>(node: T, flattenedSourceTrees: T[]): T[] {
  const nodeCopy: T = { ...node, nodes: [], scorableItems: [] };

  const tree: T[] = [nodeCopy];

  function traverseAncestors(childNode: Node): void {
    const parentNode = getParentOfNode(childNode.id, flattenedSourceTrees);
    if (!parentNode) return;

    const parentNodeCopy: T = { ...parentNode, nodes: [], scorableItems: [] };

    tree.unshift(parentNodeCopy);
    traverseAncestors(parentNode);
  }

  traverseAncestors(node);

  return tree;
}

interface Opts<T extends Node> {
  newScorableItemsStates: ScorableItemsState;
  allScorableItems: ScorableItem[];
  allNodes: T[];
  filteredNodes: T[];
}

export function listCreatorFactory<T extends Node>({
  allNodes,
  allScorableItems,
  newScorableItemsStates,
  filteredNodes,
}: Opts<T>): ListCreator<T> {
  const checkedScorableItemIds = Object.entries(newScorableItemsStates)
    .filter(([, state]) => state === 'checked')
    .map(([id]) => id);

  const checkedScorableItems = allScorableItems.filter(item => checkedScorableItemIds.includes(item.id));
  const scorableItemsPerNode = groupBy(checkedScorableItems, 'nodeId');

  const uniqueNodeIdsOfScorableItems = new Set(
    checkedScorableItemIds.map(id => allScorableItems.find(item => item.id === id)?.nodeId),
  );

  const uniqueNodes = Array.from(uniqueNodeIdsOfScorableItems)
    .map(id => allNodes.find(node => node.id === id))
    .filter((node): node is T => node !== undefined);

  const ancestorTrees = uniqueNodes.reduce<T[][]>((acc, node) => {
    const ancestorTree = buildAncestorTreeOfNode(node, filteredNodes);
    acc.push(ancestorTree);
    return acc;
  }, []);

  const listCreator: ListCreator<T> = rootNodes => {
    const res = ancestorTrees.reduce((acc, ancestorTree) => {
      let lastNode: Node | null = null;

      ancestorTree.forEach((ancestorNode, index) => {
        if (lastNode && !lastNode.nodes) lastNode.nodes = [];
        if (lastNode && !lastNode.scorableItems) lastNode.scorableItems = [];

        if (lastNode === null) {
          const rootNode = rootNodes.find(node => node.id === ancestorTree[0].id);
          if (!rootNode) {
            acc.push(ancestorNode);
            lastNode = ancestorNode;
          } else {
            lastNode = rootNode;
          }
        } else if (lastNode && lastNode.nodes?.some(node => node.id === ancestorNode.id)) {
          lastNode = lastNode.nodes.find(node => node.id === ancestorNode.id)!;
        } else if (lastNode && !lastNode.nodes?.some(node => node.id === ancestorNode.id)) {
          lastNode.nodes!.push(ancestorNode);
          lastNode = ancestorNode;
        }

        if (!lastNode) return acc;

        const originalNode = allNodes.find(node => node.id === lastNode!.id);
        if (!originalNode) return acc;

        if (originalNode.nodes) {
          lastNode.nodes?.sort(nodesSorterFactory(originalNode.nodes));
        }

        const isLast = index === ancestorTree.length - 1;
        if (isLast && scorableItemsPerNode[ancestorNode.id]) {
          const scorableItemsToAdd = (scorableItemsPerNode[ancestorNode.id] || []).filter(item => {
            return !lastNode?.scorableItems?.some(existingItem => existingItem.id === item.id);
          });

          const scorableItemsForLastNode = [...(lastNode.scorableItems || []), ...scorableItemsToAdd].sort(
            (item1, item2) => {
              return (
                originalNode.scorableItems?.findIndex(item => item.id === item1.id)! -
                originalNode.scorableItems?.findIndex(item => item.id === item2.id)!
              );
            },
          );

          lastNode.scorableItems = scorableItemsForLastNode;
        }
      });

      return acc;
    }, rootNodes);

    return res.sort(nodesSorterFactory(rootNodes));
  };

  return listCreator;
}
